import React, { useState, useEffect, useRef, useCallback } from 'react';
import { productsAPI } from '../services/api';
import './QuickEditModal.css';

/**
 * QuickEditModal — Edición rápida de productos (F2)
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onSaved: () => void  ← para refrescar la lista
 *  - sucursalId: number | null
 */
const QuickEditModal = ({ isOpen, onClose, onSaved, sucursalId = null }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);

    const [fields, setFields] = useState({
        name: '',
        sale_price: '',
        purchase_price: '',
        min_stock: '',
        laboratorio: '',
        presentacion: '',
        concentracion: '',
        principio_activo: '',
        codigo_barras: '',
        is_active: true,
    });

    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setResults([]);
            setSelectedProduct(null);
            setIsSaving(false);
            setSavedFlash(false);
            setTimeout(() => searchRef.current?.focus(), 80);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (e.key === 'Escape') { onClose(); return; }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (selectedProduct) handleSave();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, selectedProduct, fields, onClose]);

    const doSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setResults([]); return; }
        setIsSearching(true);
        try {
            const res = await productsAPI.search(term.trim(), sucursalId);
            setResults(res.data?.data?.products || []);
        } catch (err) {
            console.error('QuickEdit search error:', err);
        } finally {
            setIsSearching(false);
        }
    }, [sucursalId]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(val), 320);
    };

    const handleSelect = (product) => {
        setSelectedProduct(product);
        setSavedFlash(false);
        setFields({
            name:             product.name || '',
            sale_price:       product.sale_price ?? product.price ?? '',
            purchase_price:   product.purchase_price ?? '',
            min_stock:        product.min_stock ?? 5,
            laboratorio:      product.laboratorio || '',
            presentacion:     product.presentacion || '',
            concentracion:    product.concentracion || '',
            principio_activo: product.principio_activo || '',
            codigo_barras:    product.codigo_barras || '',
            is_active:        product.is_active !== false,
        });
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFields(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!selectedProduct || isSaving) return;
        setIsSaving(true);
        try {
            const payload = {
                ...fields,
                sale_price:     parseFloat(fields.sale_price)     || 0,
                purchase_price: parseFloat(fields.purchase_price) || 0,
                min_stock:      parseFloat(fields.min_stock)       || 0,
            };
            await productsAPI.update(selectedProduct.id, payload);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
            onSaved && onSaved();
            setSelectedProduct(prev => ({ ...prev, ...payload }));
        } catch (err) {
            alert('Error al guardar: ' + (err.response?.data?.message || 'Error desconocido'));
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const stock       = parseFloat(selectedProduct?.stock) || 0;
    const salePrice   = parseFloat(fields.sale_price)      || 0;
    const purchPrice  = parseFloat(fields.purchase_price)  || 0;
    const ganancia    = salePrice - purchPrice;
    const gananciaPct = purchPrice > 0 ? ((ganancia / purchPrice) * 100).toFixed(0) : '—';

    return (
        <div className="qem-overlay" onClick={onClose}>
            <div className="qem-modal" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="qem-header">
                    <div className="qem-header-left">
                        <span className="qem-header-icon">⚡</span>
                        <div>
                            <div className="qem-header-title">Edición Rápida de Producto</div>
                            <div className="qem-header-sub">Busca un producto y edita sus datos al instante</div>
                        </div>
                    </div>
                    <div className="qem-header-actions">
                        <span className="qem-shortcut-pill"><kbd>F2</kbd> abrir · <kbd>Esc</kbd> cerrar · <kbd>Ctrl+S</kbd> guardar</span>
                        <button className="qem-close-btn" onClick={onClose}>✕ Cerrar (Esc)</button>
                    </div>
                </div>

                {/* BODY: 2 columnas */}
                <div className="qem-body">

                    {/* COLUMNA IZQUIERDA — BUSCADOR */}
                    <div className="qem-left">
                        <div className="qem-search-title">🔍 Buscar producto</div>
                        <div className="qem-search-wrap">
                            <input
                                ref={searchRef}
                                type="text"
                                className="qem-search-input"
                                placeholder="Nombre, código SKU, laboratorio..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                autoComplete="off"
                            />
                            {isSearching && <span className="qem-spin">⟳</span>}
                            {searchTerm && (
                                <button
                                    className="qem-search-clear"
                                    onClick={() => { setSearchTerm(''); setResults([]); searchRef.current?.focus(); }}
                                >✕</button>
                            )}
                        </div>

                        <div className="qem-results-list">
                            {results.length === 0 && searchTerm.length >= 2 && !isSearching && (
                                <div className="qem-no-results">Sin resultados para «{searchTerm}»</div>
                            )}
                            {results.length === 0 && searchTerm.length < 2 && (
                                <div className="qem-results-hint">Escribe al menos 2 caracteres para buscar...</div>
                            )}
                            {results.map(p => (
                                <div
                                    key={p.id}
                                    className={`qem-result-item ${selectedProduct?.id === p.id ? 'active' : ''} ${p.stock <= 0 ? 'agotado' : ''}`}
                                    onClick={() => handleSelect(p)}
                                >
                                    <div className="qem-ri-info">
                                        <div className="qem-ri-name">{p.name}</div>
                                        <div className="qem-ri-meta">
                                            {p.code}
                                            {p.laboratorio && <> · {p.laboratorio}</>}
                                            {p.concentracion && <> · {p.concentracion}</>}
                                        </div>
                                    </div>
                                    <div className="qem-ri-right">
                                        <span className={`qem-stock-chip ${p.stock <= 0 ? 'out' : p.stock <= p.min_stock ? 'low' : 'ok'}`}>
                                            {p.stock <= 0 ? 'Agotado' : `${p.stock} ${p.unit || 'u'}`}
                                        </span>
                                        <span className="qem-price-chip">S/. {parseFloat(p.sale_price || p.price || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* COLUMNA DERECHA — EDITOR */}
                    <div className="qem-right">
                        {!selectedProduct ? (
                            <div className="qem-no-selection">
                                <div className="qem-no-sel-icon">📋</div>
                                <div className="qem-no-sel-title">Ningún producto seleccionado</div>
                                <div className="qem-no-sel-sub">Busca y haz clic en un producto de la izquierda para editar sus datos aquí</div>
                            </div>
                        ) : (
                            <div className="qem-editor">

                                {/* Info card del producto */}
                                <div className={`qem-product-card ${savedFlash ? 'flash-saved' : ''}`}>
                                    <div className="qem-pc-top">
                                        <span className="qem-pc-code">{selectedProduct.code}</span>
                                        <span className={`qem-pc-status ${fields.is_active ? 'active' : 'inactive'}`}>
                                            ● {fields.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                        {savedFlash && <span className="qem-saved-badge">✅ Guardado</span>}
                                    </div>
                                    <div className="qem-pc-name">{fields.name || selectedProduct.name}</div>
                                    <div className="qem-pc-stock">
                                        Stock actual: <strong>{stock} {selectedProduct.unit || 'u'}</strong>
                                        {stock <= 0 && <span className="qem-badge-agotado">AGOTADO</span>}
                                        {stock > 0 && stock <= (selectedProduct.min_stock || 5) && <span className="qem-badge-low">STOCK BAJO</span>}
                                    </div>
                                </div>

                                {/* Campos editables en grid */}
                                <div className="qem-fields-grid">

                                    {/* Nombre — fila completa */}
                                    <div className="qem-field qem-field--full">
                                        <label className="qem-flabel">📝 Nombre del Producto</label>
                                        <input type="text" name="name" className="qem-finput" value={fields.name} onChange={handleFieldChange} />
                                    </div>

                                    {/* Precios */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">💰 Precio de Compra</label>
                                        <div className="qem-price-row">
                                            <span className="qem-currency">S/.</span>
                                            <input type="number" step="0.01" min="0" name="purchase_price" className="qem-finput qem-finput--price" value={fields.purchase_price} onChange={handleFieldChange} onFocus={e => e.target.select()} />
                                        </div>
                                    </div>
                                    <div className="qem-field">
                                        <label className="qem-flabel">🏷️ Precio de Venta</label>
                                        <div className="qem-price-row">
                                            <span className="qem-currency">S/.</span>
                                            <input type="number" step="0.01" min="0" name="sale_price" className="qem-finput qem-finput--price" value={fields.sale_price} onChange={handleFieldChange} onFocus={e => e.target.select()} />
                                        </div>
                                    </div>

                                    {/* Ganancia — fila completa */}
                                    <div className="qem-profit-row">
                                        <span>Ganancia estimada:</span>
                                        <strong className={ganancia >= 0 ? 'profit-pos' : 'profit-neg'}>
                                            S/. {ganancia.toFixed(2)} ({gananciaPct}%)
                                        </strong>
                                    </div>

                                    {/* Stock mínimo */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">🔔 Stock Mínimo (alerta)</label>
                                        <input type="number" min="0" name="min_stock" className="qem-finput" value={fields.min_stock} onChange={handleFieldChange} onFocus={e => e.target.select()} />
                                    </div>

                                    {/* Laboratorio */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">🏭 Laboratorio / Marca</label>
                                        <input type="text" name="laboratorio" className="qem-finput" value={fields.laboratorio} onChange={handleFieldChange} placeholder="Ej: Genfar, MK..." />
                                    </div>

                                    {/* Presentación */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">💊 Presentación</label>
                                        <input type="text" name="presentacion" className="qem-finput" value={fields.presentacion} onChange={handleFieldChange} placeholder="Ej: Caja x 30 tab..." />
                                    </div>

                                    {/* Concentración */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">🧪 Concentración / Dosis</label>
                                        <input type="text" name="concentracion" className="qem-finput" value={fields.concentracion} onChange={handleFieldChange} placeholder="Ej: 500mg..." />
                                    </div>

                                    {/* Principio activo */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">🔬 Principio Activo</label>
                                        <input type="text" name="principio_activo" className="qem-finput" value={fields.principio_activo} onChange={handleFieldChange} placeholder="Ej: Paracetamol..." />
                                    </div>

                                    {/* Código de barras */}
                                    <div className="qem-field">
                                        <label className="qem-flabel">▌▌▌ Código de Barras</label>
                                        <input type="text" name="codigo_barras" className="qem-finput" value={fields.codigo_barras} onChange={handleFieldChange} placeholder="EAN-13..." maxLength={50} />
                                    </div>

                                    {/* Estado */}
                                    <div className="qem-field qem-field--full">
                                        <label className="qem-flabel">Estado</label>
                                        <div className="qem-status-toggle">
                                            <button
                                                type="button"
                                                className={`qem-toggle ${fields.is_active ? 'is-active' : ''}`}
                                                onClick={() => setFields(p => ({ ...p, is_active: true }))}
                                            >● Activo</button>
                                            <button
                                                type="button"
                                                className={`qem-toggle ${!fields.is_active ? 'is-inactive' : ''}`}
                                                onClick={() => setFields(p => ({ ...p, is_active: false }))}
                                            >● Inactivo</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Barra de guardar */}
                                <div className="qem-save-bar">
                                    <span className="qem-save-hint"><kbd>Ctrl</kbd>+<kbd>S</kbd> para guardar rápido</span>
                                    <button
                                        className={`qem-save-btn ${savedFlash ? 'saved' : ''}`}
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {savedFlash ? '✅ ¡Cambios guardados!' : isSaving ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickEditModal;
